'use client';

import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ShoppingCart, ShoppingBag } from 'lucide-react';
import { buildStoreThemeVariables } from '@/lib/store-theme';

type AddToCartDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productName: string;
  storeId: string;
  publicStoreId?: string;
  locale: string;
  translations: {
    title: string;
    goToCart: string;
    continueShopping: string;
  };
};

export function AddToCartDialog({
  open,
  onOpenChange,
  productName,
  storeId,
  publicStoreId,
  locale,
  translations,
}: AddToCartDialogProps) {
  const router = useRouter();
  const [storeTheme, setStoreTheme] = useState<React.CSSProperties | undefined>(undefined);

  useEffect(() => {
    if (!open || !storeId) return;
    let cancelled = false;
    fetch(`/api/stores/${storeId}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((store: { color?: string | null } | null) => {
        if (cancelled || !store) return;
        const theme = buildStoreThemeVariables(store.color ?? null);
        setStoreTheme(theme ?? undefined);
      })
      .catch(() => { });
    return () => {
      cancelled = true;
    };
  }, [open, storeId]);

  const handleGoToCart = () => {
    onOpenChange(false);
    router.push(`/${locale}/${publicStoreId ?? storeId}/cart`);
  };

  const handleContinueShopping = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-2xl" style={storeTheme}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-primary" />
            {translations.title}
          </DialogTitle>
          <DialogDescription className="pt-2">
            {productName}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="flex-col sm:flex-col gap-2 mt-4 w-full px-0 sm:space-x-0 space-x-0">
          <Button
            onClick={handleGoToCart}
            className="w-full ml-0 rounded-full"
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            {translations.goToCart}
          </Button>
          <Button
            onClick={handleContinueShopping}
            variant="outline"
            className="w-full ml-0 rounded-full"
          >
            {translations.continueShopping}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
